/**
 * Solarkontor Produktvergleich
 * Vollständige Version für assets/produktvergleich.js
 */

(function () {
  'use strict';

  const state = {
    slots: [null, null, null],
    category: null,
    categoryFromURL: false,
    isLoading: [false, false, false],
    searchTimers: [null, null, null],
    recommendationCache: {}
  };

  const collectionByCategory = {
    Solarmodul: 'solarmodule',
    Powerstation: 'powerstation',
    Batterie: 'batterien'
  };

  // ---------------------------------------------------------------------------
  // Initialisierung
  // ---------------------------------------------------------------------------

  function init() {
    const config = window.SOLARKONTOR_COMPARE;
    if (!config || !config.settings) return;

    state.category = normalizeCategory(config.settings.defaultCategory);

    initURLParams();
    initSearchListeners();
    initResetButton();
    ensureRecommendationContainers();
    injectRecommendationStyles();

    updateCategoryLabel();
    updateUI();
    refreshRecommendations();
  }

  // ---------------------------------------------------------------------------
  // Kategorie-Logik
  // ---------------------------------------------------------------------------

  function getCategoryMap() {
    return window.SOLARKONTOR_COMPARE?.settings?.categoryMap || {};
  }

  function findMappedCategory(value) {
    const candidate = String(value || '').trim();
    if (!candidate) return null;

    const map = getCategoryMap();
    if (map[candidate]) return map[candidate];

    const key = Object.keys(map).find((mapKey) => {
      return mapKey.trim().toLowerCase() === candidate.toLowerCase();
    });

    return key ? map[key] : null;
  }

  function normalizeCategory(value) {
    const category = String(value || '').trim();
    if (!category) return '';

    const configs = window.SOLARKONTOR_COMPARE?.categoryConfig || {};
    if (Object.prototype.hasOwnProperty.call(configs, category)) {
      return category;
    }

    return findMappedCategory(category) || category;
  }

  function resolveCategory(product) {
    if (!product) return null;

    const productType = String(product.product_type || product.type || '').trim();
    const productTypeLower = productType.toLowerCase();
    const title = String(product.title || '').trim().toLowerCase();
    const handle = String(product.handle || '').trim().toLowerCase();
    const identity = `${productTypeLower} ${title} ${handle}`;
    const metafields = product.metafields || {};

    const mappedProductType = findMappedCategory(productType);
    const metafieldType = String(metafields.produkttyp || '').trim().toLowerCase();
    const mappedMetafieldType = findMappedCategory(metafieldType);
    const solarModuleType = String(
      metafields.solarmodultyp || metafields.solarmodul_typ || ''
    ).trim().toLowerCase();

    // Explizite Ausschlüsse müssen vor den Batterie- und Solarregeln kommen.
    // Sonst kann ein Wechselrichter mit Batterie-/Spannungsfeldern als Batterie
    // oder ein Charger mit Leistungswerten als Solarmodul erscheinen.
    const excludedTerms = [
      'wechselrichter',
      'inverter',
      'charger',
      'alternator',
      'ladegerät',
      'ladegeraet',
      'laderegler',
      'kombigerät',
      'kombigeraet',
      'wandler',
      'adapter'
    ];

    if (excludedTerms.some((term) => identity.includes(term))) {
      return null;
    }

    // Powerstation: explizite Signale haben Vorrang.
    if (
      productTypeLower === 'powerstation' ||
      mappedProductType === 'Powerstation' ||
      mappedMetafieldType === 'Powerstation' ||
      title.includes('powerstation') ||
      title.includes('power station')
    ) {
      return 'Powerstation';
    }

    const batteryIdentitySignal = [
      'batterie',
      'batteriespeicher',
      'lifepo4',
      'akku',
      'agm',
      'gelbatterie',
      'gel batterie',
      'blei',
      'lead acid',
      'deep cycle'
    ].some((term) => identity.includes(term));

    const batterySignals = [
      metafields.batteriekapazitaet_ah,
      metafields.batteriekapazitat_ah,
      metafields.batterie_typ,
      metafields.batterietyp,
      metafields.batteriespannung,
      metafields.ladezyklen
    ];

    // Ein Batterie-Metafeld allein reicht nicht. Es muss zusätzlich ein
    // plausibles Batteriesignal im Typ, Titel oder Handle vorhanden sein.
    if (
      batteryIdentitySignal &&
      (
        batterySignals.some(hasValue) ||
        mappedProductType === 'Batterie' ||
        mappedMetafieldType === 'Batterie'
      )
    ) {
      return 'Batterie';
    }

    const hasExplicitSolarType = [
      'solarmodul',
      'solarkoffer',
      'solartasche',
      'solarpanel',
      'solar panel',
      'balkonkraftwerk',
      'photovoltaik'
    ].some((term) => identity.includes(term)) || [
      'gerahmt',
      'flexibel',
      'mobil'
    ].some((term) => solarModuleType.includes(term));

    if (hasExplicitSolarType) {
      return 'Solarmodul';
    }

    // Leistung allein ist kein Solarmodul-Signal.
    const hasSolarTechnicalSignal =
      hasValue(metafields.solarzellentyp) ||
      hasValue(metafields.modulwirkungsgrad) ||
      (
        hasValue(metafields.leistung_w) &&
        hasValue(metafields.spannung_umpp) &&
        hasValue(metafields.strom_impp)
      );

    if (hasSolarTechnicalSignal) {
      return 'Solarmodul';
    }

    const powerstationSignals = [
      metafields.kapazitaet_wh,
      metafields.dauerleistung_ac,
      metafields.max_pv_eingangsleistung_w,
      metafields.dc_eingang_solar,
      metafields.ausgangsspannung_dc,
      metafields.ac_ausgangsspannung,
      metafields.anschlusse_notstrom,
      metafields.ladeschlussspannung
    ];

    if (powerstationSignals.some(hasValue)) {
      return 'Powerstation';
    }

    return null;
  }

  function getActiveCategoryConfig() {
    const config = window.SOLARKONTOR_COMPARE;
    if (!config) return [];

    const categoryConfig = config.categoryConfig || {};
    const possibleCategories = [
      normalizeCategory(state.category || config.settings.defaultCategory),
      ...state.slots
        .filter(Boolean)
        .map((product) => resolveCategory(product))
        .filter(Boolean)
    ];

    for (const category of possibleCategories) {
      if (
        category &&
        Array.isArray(categoryConfig[category]) &&
        categoryConfig[category].length > 0
      ) {
        return categoryConfig[category];
      }
    }

    return [];
  }

  // ---------------------------------------------------------------------------
  // URL-Parameter und Produktdaten
  // ---------------------------------------------------------------------------

  async function initURLParams() {
    const params = new URLSearchParams(window.location.search);

    if (params.get('category')) {
      state.category = normalizeCategory(params.get('category'));
      state.categoryFromURL = true;
    }

    const handles = [params.get('p'), params.get('p2'), params.get('p3')];

    // Sequenziell laden, damit Slot 1 bei fehlender Kategorie-URL
    // die Kategorie für die weiteren Slots bestimmen kann.
    for (let index = 0; index < handles.length; index += 1) {
      if (handles[index]) {
        await loadProductIntoSlot(index, handles[index]);
      }
    }
  }

  function normalizeMetafields(metafields) {
    if (!metafields || typeof metafields !== 'object') return {};

    return Object.fromEntries(
      Object.entries(metafields).map(([key, value]) => {
        if (
          value &&
          typeof value === 'object' &&
          Object.prototype.hasOwnProperty.call(value, 'value')
        ) {
          return [key, value.value];
        }

        return [key, value];
      })
    );
  }

function getCanonicalProductHandle(product) {
  const source = product?.url || product?.handle || '';

  if (!source) return '';

  try {
    const pathname = new URL(source, window.location.origin).pathname;
    const match = pathname.match(/^\/products\/(.+)$/i);

    if (match) {
      return decodeURIComponent(match[1]);
    }
  } catch (error) {
    // Fallback auf den vorhandenen Handle
  }

  return String(product?.handle || '').trim();
}

  async function fetchProductData(handle) {
    const originalHandle = String(handle || '').trim();
    if (!originalHandle) return null;

    let normalizedHandle = originalHandle;
    try {
      normalizedHandle = decodeURIComponent(originalHandle).trim();
    } catch (error) {
      normalizedHandle = originalHandle;
    }

    const url = `/products/${encodeURIComponent(normalizedHandle)}?view=compare-data&_pv=${Date.now()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const responseText = await response.text();
      let responseData;

      try {
        responseData = JSON.parse(responseText);
      } catch (error) {
        console.error('[Vergleich] Endpoint liefert kein gültiges JSON.');
        return null;
      }

      // Unterstützt direkte und unter product verschachtelte Antworten.
      const data = responseData?.product || responseData;

      if (
        data &&
        data.handle &&
        data.variant &&
        data.metafields &&
        typeof data.metafields === 'object'
      ) {
        return {
          ...data,
          metafields: normalizeMetafields(data.metafields),
          image: getProductImage(data)
        };
      }

      console.warn('[Vergleich] Endpoint liefert keine verwendbare Produktstruktur:', responseData);
      return null;
    } catch (error) {
      console.error('[Vergleich] Fehler beim Laden von:', normalizedHandle, error);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Empfehlungen aus Collections
  // ---------------------------------------------------------------------------

  function ensureRecommendationContainers() {
    document.querySelectorAll('.pv-slot__empty').forEach((emptySlot) => {
      if (emptySlot.querySelector('.pv-recommendations')) return;

      const container = document.createElement('div');
      container.className = 'pv-recommendations hidden';
      container.setAttribute('aria-live', 'polite');
      emptySlot.appendChild(container);
    });
  }

  function injectRecommendationStyles() {
    if (document.getElementById('pvRecommendationStyles')) return;

    const style = document.createElement('style');
    style.id = 'pvRecommendationStyles';
    style.textContent = `
      .pv-recommendations { width: 100%; margin-top: .25rem; text-align: left; }
      .pv-recommendations__title { margin: 0 0 .55rem; font-size: .78rem; font-weight: 700; color: var(--pv-color-text, #1a1a1a); }
      .pv-recommendations__list { display: flex; flex-direction: column; gap: .35rem; max-height: 220px; overflow-y: auto; }
      .pv-recommendations__item { display: grid; grid-template-columns: 34px 1fr auto; align-items: center; gap: .5rem; width: 100%; padding: .35rem .45rem; border: 1px solid var(--pv-color-border, #e0e0e0); border-radius: 5px; background: var(--pv-color-bg, #fff); color: var(--pv-color-text, #1a1a1a); text-align: left; cursor: pointer; font: inherit; }
      .pv-recommendations__item:hover, .pv-recommendations__item:focus { border-color: var(--pv-color-accent, #f5a623); background: var(--pv-color-surface, #f5f5f5); outline: none; }
      .pv-recommendations__image { width: 34px; height: 34px; object-fit: contain; border-radius: 3px; background: var(--pv-color-surface, #f5f5f5); }
      .pv-recommendations__name { min-width: 0; overflow: hidden; font-size: .74rem; line-height: 1.25; }
      .pv-recommendations__title-line { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .pv-recommendations__meta { display: block; margin-top: .12rem; color: var(--pv-color-muted, #6b6b6b); font-size: .68rem; line-height: 1.2; white-space: nowrap; }
      .pv-recommendations__series { margin-left: .35rem; }
      .pv-recommendations__price { white-space: nowrap; color: var(--pv-color-muted, #6b6b6b); font-size: .72rem; }
      .pv-recommendations__loading, .pv-recommendations__empty { color: var(--pv-color-muted, #6b6b6b); font-size: .75rem; }
      @media (max-width: 600px) { .pv-recommendations__list { max-height: 260px; } }
    `;
    document.head.appendChild(style);
  }

  function getCollectionHandle() {
    return collectionByCategory[normalizeCategory(state.category)] || null;
  }

  function getAnchorProduct() {
    return state.slots.find(Boolean) || null;
  }

  async function refreshRecommendations() {
    ensureRecommendationContainers();

    const emptySlots = [...document.querySelectorAll('.pv-slot__empty')]
      .filter((empty) => !empty.classList.contains('hidden'));

    if (!emptySlots.length) return;

    // Progressive UX: Empfehlungen nur im ersten freien Slot anzeigen.
    // Die Suche bleibt in allen freien Slots verfügbar.
    const activeEmptySlot = emptySlots[0];

    emptySlots.slice(1).forEach((empty) => {
      const container = empty.querySelector('.pv-recommendations');
      if (container) {
        container.innerHTML = '';
        container.classList.add('hidden');
      }
    });

    const collectionHandle = getCollectionHandle();
    const activeContainer = activeEmptySlot.querySelector('.pv-recommendations');

    if (!collectionHandle) {
      if (activeContainer) {
        activeContainer.innerHTML = '';
        activeContainer.classList.add('hidden');
      }
      return;
    }

    const selectedHandles = state.slots
      .filter(Boolean)
      .map((product) => product.handle)
      .join('|');
    const series = getAnchorProduct()?.metafields?.serie || '';
    const cacheKey = `${collectionHandle}:${selectedHandles}:${series}`;
    let recommendations = state.recommendationCache[cacheKey];

    if (!recommendations) {
      if (activeContainer) {
        activeContainer.innerHTML = '<div class="pv-recommendations__loading">Passende Produkte werden geladen ...</div>';
        activeContainer.classList.remove('hidden');
      }

      recommendations = await loadCollectionRecommendations(collectionHandle);
      state.recommendationCache[cacheKey] = recommendations;
    }

    const slot = activeEmptySlot.closest('.pv-slot')?.dataset.slot;
    if (activeContainer) {
      renderRecommendations(activeContainer, recommendations, slot);
    }
  }

  async function loadCollectionRecommendations(collectionHandle) {
    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const selectedHandles = new Set(
        state.slots.filter(Boolean).map((product) => product.handle)
      );
      const anchorSeries = String(
        getAnchorProduct()?.metafields?.serie || ''
      ).trim().toLowerCase();

      const candidates = (data.products || [])
        .filter((product) => !selectedHandles.has(product.handle))
        .filter((product) => product.available !== false)
        .slice(0, 40);

      const enriched = await Promise.all(candidates.map(async (candidate) => {
        const canonicalHandle = getCanonicalProductHandle(candidate);
        const compareData = await fetchProductData(canonicalHandle);
        if (!compareData) return null;

        const resolvedCategory = resolveCategory(compareData);
        if (resolvedCategory !== normalizeCategory(state.category)) return null;

        const candidateSeries = String(
          compareData.metafields?.serie || ''
        ).trim();
        const sameSeries = Boolean(
          anchorSeries && candidateSeries.toLowerCase() === anchorSeries
        );

        return {
          ...candidate,
          compareData,
          series: candidateSeries,
          sameSeries,
          price: Number(compareData.variant?.price || 0)
        };
      }));

      return enriched
        .filter(Boolean)
        .sort((a, b) => {
          if (a.sameSeries !== b.sameSeries) return a.sameSeries ? -1 : 1;
          if (a.series !== b.series) return a.series.localeCompare(b.series, 'de');
          return a.price - b.price;
        })
        .slice(0, 8);
    } catch (error) {
      console.error('[Vergleich] Empfehlungen konnten nicht geladen werden:', error);
      return [];
    }
  }

  function getCategoryPlural(category) {
    const labels = {
      Solarmodul: 'Solarmodule',
      Powerstation: 'Powerstations',
      Batterie: 'Batterien'
    };

    return labels[normalizeCategory(category)] || 'Produkte';
  }

  function getRecommendationMetric(product) {
    const category = normalizeCategory(state.category);
    const metafields = product.compareData?.metafields || {};

    if (category === 'Solarmodul' && hasValue(metafields.leistung_w)) {
      return `${formatNumber(metafields.leistung_w)} Wp`;
    }

    if (category === 'Powerstation' && hasValue(metafields.kapazitaet_wh)) {
      return `${formatNumber(metafields.kapazitaet_wh)} Wh`;
    }

    if (category === 'Batterie' && hasValue(metafields.batteriekapazitaet_ah)) {
      return `${formatNumber(metafields.batteriekapazitaet_ah)} Ah`;
    }

    return '';
  }

  function formatNumber(value) {
    const number = parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(number)) return String(value || '');
    return Number.isInteger(number)
      ? String(number)
      : number.toLocaleString('de-DE', { maximumFractionDigits: 1 });
  }

  function renderRecommendations(container, products, slotIndex) {
    if (!products.length) {
      container.innerHTML = '<div class="pv-recommendations__empty">Keine weiteren passenden Produkte gefunden.</div>';
      container.classList.remove('hidden');
      return;
    }

    container.innerHTML = `
      <p class="pv-recommendations__title">Weitere ${escapeHTML(getCategoryPlural(state.category))}</p>
      <div class="pv-recommendations__list">
        ${products.map((product) => {
          const image = getProductImage({ ...product, ...product.compareData });
          const price = product.compareData?.variant?.price
            ? formatMoney(product.compareData.variant.price)
            : '';
          const metric = getRecommendationMetric(product);
          const series = product.series
            ? `<span class="pv-recommendations__series">· ${escapeHTML(product.series)}</span>`
            : '';
          const meta = [metric, series].filter(Boolean).join(' ');

          return `
            <button
              class="pv-recommendations__item"
              type="button"
              title="${escapeHTML(product.title)}"
              data-recommendation-handle="${escapeHTML(
              getCanonicalProductHandle(product.compareData || product)
        )}"
              data-recommendation-slot="${escapeHTML(slotIndex)}"
            >
              ${image ? `<img class="pv-recommendations__image" src="${escapeHTML(image)}" alt="" loading="lazy">` : '<span></span>'}
              <span class="pv-recommendations__name">
                <span class="pv-recommendations__title-line">${escapeHTML(product.title)}</span>
                ${meta ? `<span class="pv-recommendations__meta">${meta}</span>` : ''}
              </span>
              <span class="pv-recommendations__price">${price}</span>
            </button>
          `;
        }).join('')}
      </div>
    `;

    container.querySelectorAll('[data-recommendation-handle]').forEach((button) => {
      button.addEventListener('click', () => {
        loadProductIntoSlot(
          Number.parseInt(button.dataset.recommendationSlot, 10),
          button.dataset.recommendationHandle
        );
      });
    });

    container.classList.remove('hidden');
  }

  // ---------------------------------------------------------------------------
  // Slot-Management und Produktkarten
  // ---------------------------------------------------------------------------

  async function loadProductIntoSlot(slotIndex, handle) {
    if (slotIndex < 0 || slotIndex > 2 || !handle) return;

    const alreadyLoaded = state.slots.some((product, index) => {
      return product && product.handle === handle && index !== slotIndex;
    });

    if (alreadyLoaded) {
      showToast('Dieses Produkt ist bereits im Vergleich.', 'warning');
      return;
    }

    state.isLoading[slotIndex] = true;
    setSlotLoading(slotIndex, true);

    const product = await fetchProductData(handle);

    state.isLoading[slotIndex] = false;
    setSlotLoading(slotIndex, false);

    if (!product) {
      showToast('Produkt konnte nicht geladen werden.', 'error');
      return;
    }

    const productCategory = resolveCategory(product);
    const hasLoadedProduct = state.slots.some(Boolean);

    if (!state.categoryFromURL && !hasLoadedProduct && productCategory) {
      state.category = productCategory;
      updateCategoryLabel();
    }

    const activeCategory = normalizeCategory(state.category);
    if (
      productCategory &&
      activeCategory &&
      productCategory !== activeCategory
    ) {
      showToast(
        `Dieses Produkt gehört zur Kategorie „${productCategory}" und kann hier nicht verglichen werden.`,
        'warning'
      );
      return;
    }

    state.slots[slotIndex] = product;
    renderSlotFilled(slotIndex, product);
    updateURL();
    updateUI();
    renderComparisonTable();
    refreshRecommendations();
  }

  function removeProductFromSlot(slotIndex) {
    state.slots[slotIndex] = null;
    clearSlot(slotIndex);
    updateURL();
    updateUI();
    renderComparisonTable();
    refreshRecommendations();
  }

  function clearAllSlots() {
    state.slots = [null, null, null];

    if (!state.categoryFromURL) {
      state.category = normalizeCategory(
        window.SOLARKONTOR_COMPARE.settings.defaultCategory
      );
    }

    for (let index = 0; index < 3; index += 1) clearSlot(index);

    updateURL();
    updateUI();
    renderComparisonTable();
    refreshRecommendations();
  }

  function clearSlot(slotIndex) {
    const filled = document.getElementById(`pvSlotFilled${slotIndex}`);
    const empty = document.getElementById(`pvSlotEmpty${slotIndex}`);

    if (filled) {
      filled.innerHTML = '';
      filled.classList.add('hidden');
    }

    if (empty) {
      empty.classList.remove('hidden');
      const input = empty.querySelector('.pv-search__input');
      if (input) input.value = '';
      const results = empty.querySelector('.pv-search__results');
      if (results) {
        results.innerHTML = '';
        results.classList.add('hidden');
      }
    }
  }

  function setSlotLoading(slotIndex, isLoading) {
    document.getElementById(`pvSlot${slotIndex}`)
      ?.classList.toggle('pv-slot--loading', isLoading);
  }

  function renderSlotFilled(slotIndex, product) {
    const empty = document.getElementById(`pvSlotEmpty${slotIndex}`);
    const filled = document.getElementById(`pvSlotFilled${slotIndex}`);
    if (!empty || !filled) return;

    const variant = product.variant || {};
    const price = formatMoney(variant.price);
    const compareAtPrice = Number(variant.compare_at_price) > Number(variant.price)
      ? formatMoney(variant.compare_at_price)
      : null;
    const imageUrl = getProductImage(product);

    const imageHTML = imageUrl
      ? `<img class="pv-card__image" src="${escapeHTML(imageUrl)}" alt="${escapeHTML(product.title)}" loading="lazy" width="200" height="200">`
      : `<div class="pv-card__image-placeholder" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <path d="M21 15l-5-5L5 21"/>
          </svg>
        </div>`;

    filled.innerHTML = `
      <div class="pv-card">
        <button class="pv-card__remove" data-slot="${slotIndex}" aria-label="${escapeHTML(product.title)} aus Vergleich entfernen">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
        <a href="${escapeHTML(product.url)}" class="pv-card__image-link" tabindex="-1" aria-hidden="true">
          ${imageHTML}
        </a>
        <div class="pv-card__body">
          <a href="${escapeHTML(product.url)}" class="pv-card__title">${escapeHTML(product.title)}</a>
          <div class="pv-card__price">
            <span class="pv-card__price-current">${price}</span>
            ${compareAtPrice ? `<span class="pv-card__price-compare">${compareAtPrice}</span>` : ''}
          </div>
          ${!variant.available ? '<span class="pv-card__unavailable">Nicht auf Lager</span>' : ''}
          <div class="pv-card__actions">
            <button class="pv-card__cart-btn button button--primary" data-variant-id="${escapeHTML(variant.id)}" ${!variant.available ? 'disabled aria-disabled="true"' : ''}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              ${variant.available ? 'In den Warenkorb' : 'Nicht verfügbar'}
            </button>
            <a href="${escapeHTML(product.url)}" class="pv-card__pdp-link button button--secondary">Zum Produkt</a>
          </div>
        </div>
      </div>
    `;

    filled.querySelector('.pv-card__remove')?.addEventListener('click', () => removeProductFromSlot(slotIndex));
    filled.querySelector('.pv-card__cart-btn')?.addEventListener('click', (event) => addToCart(variant.id, event.currentTarget));

    const imageElement = filled.querySelector('.pv-card__image');
    imageElement?.addEventListener('error', () => {
      imageElement.remove();
      const placeholder = document.createElement('div');
      placeholder.className = 'pv-card__image-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = 'Bild nicht verfügbar';
      imageElement.parentElement?.appendChild(placeholder);
    }, { once: true });

    empty.classList.add('hidden');
    filled.classList.remove('hidden');
  }

  // ---------------------------------------------------------------------------
  // Suche
  // ---------------------------------------------------------------------------

  function initSearchListeners() {
    for (let index = 0; index < 3; index += 1) {
      const input = document.querySelector(`.pv-search__input[data-slot="${index}"]`);
      const clearButton = document.querySelector(`.pv-search__clear[data-slot="${index}"]`);
      if (!input) continue;

      input.addEventListener('input', (event) => {
        const query = event.target.value.trim();
        clearButton?.classList.toggle('hidden', query.length === 0);
        debouncedSearch(index, query);
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          hideSearchResults(index);
          input.blur();
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          focusFirstResult(index);
        }
      });

      clearButton?.addEventListener('click', () => {
        input.value = '';
        clearButton.classList.add('hidden');
        hideSearchResults(index);
        input.focus();
      });
    }

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.pv-search')) {
        for (let index = 0; index < 3; index += 1) hideSearchResults(index);
      }
    });
  }

  function debouncedSearch(slotIndex, query) {
    clearTimeout(state.searchTimers[slotIndex]);
    if (query.length < 2) {
      hideSearchResults(slotIndex);
      return;
    }
    state.searchTimers[slotIndex] = setTimeout(() => searchProducts(slotIndex, query), 300);
  }

  async function searchProducts(slotIndex, query) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[options][fields]': 'title,product_type,variants.title',
      'resources[options][limit]': 10,
      'resources[options][unavailable_products]': 'last'
    });

    try {
      const response = await fetch(`/search/suggest.json?${params}`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const loadedHandles = new Set(
        state.slots.filter(Boolean).map((product) => product.handle)
      );

      let products = (data?.resources?.results?.products || [])
        .filter((product) => {
          const handle = getCanonicalProductHandle(product);
          return handle && !loadedHandles.has(handle);
        });

      // Shopify Predictive Search findet „batte“ nicht zuverlässig,
      // wenn der Begriff nur in der Kategorie, nicht aber im Produkttitel steht.
      // Deshalb wird bei leerem Ergebnis zusätzlich die aktive Collection durchsucht.
      if (!products.length) {
        products = await loadCategorySearchFallback(query, loadedHandles);
      }

      products = await filterSearchProductsByCategory(products);

      // Auch wenn Predictive Search zunächst Treffer liefert, können diese
      // nach der strengen Kategorieprüfung vollständig herausfallen. In diesem
      // Fall nochmals über die aktive Collection suchen.
      if (!products.length) {
        const fallbackProducts = await loadCategorySearchFallback(query, loadedHandles);
        products = await filterSearchProductsByCategory(fallbackProducts);
      }

      renderSearchResults(slotIndex, products);
    } catch (error) {
      console.error('[Vergleich] Suchfehler:', error);
      renderSearchResults(slotIndex, []);
    }
  }

  async function loadCategorySearchFallback(query, loadedHandles) {
    const collectionHandle = getCollectionHandle();
    if (!collectionHandle) return [];

    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const queryLower = String(query || '').trim().toLowerCase();
      const categoryTerms = {
        Batterie: ['bat', 'akku', 'speicher', 'agm', 'lifepo', 'blei', 'gel', 'deep'],
        Powerstation: ['power', 'station', 'stromspeicher'],
        Solarmodul: ['solar', 'modul', 'panel', 'pv', 'photovoltaik']
      };
      const isCategoryQuery = (categoryTerms[normalizeCategory(state.category)] || [])
        .some((term) => queryLower.includes(term));

      return (data.products || [])
        .filter((product) => {
          const handle = getCanonicalProductHandle(product);
          if (!handle || loadedHandles.has(handle)) return false;

          const text = [
            product.title,
            product.handle,
            product.product_type,
            ...(product.variants || []).map((variant) => variant.title)
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return isCategoryQuery || text.includes(queryLower);
        })
        .slice(0, 60);
    } catch (error) {
      console.error('[Vergleich] Fallback-Suche fehlgeschlagen:', error);
      return [];
    }
  }

  async function filterSearchProductsByCategory(products) {
    const targetCategory = normalizeCategory(state.category);
    if (!targetCategory) return products;

    const checked = await Promise.all(products.map(async (product) => {
      const handle = getCanonicalProductHandle(product);
      if (!handle) return null;

      // Immer die vollständigen Vergleichsdaten prüfen. Ein product_type aus
      // der Predictive Search darf die Kategorie nicht blind überschreiben.
      const compareData = await fetchProductData(handle);
      if (!compareData) return null;

      return resolveCategory(compareData) === targetCategory
        ? { ...product, handle }
        : null;
    }));

    return checked.filter(Boolean);
  }

  function renderSearchResults(slotIndex, products) {
    const resultsList = document.getElementById(`pvSearchResults${slotIndex}`);
    if (!resultsList) return;

    if (!products.length) {
      resultsList.innerHTML = '<li class="pv-search__no-results" role="option">Keine passenden Produkte gefunden</li>';
      resultsList.classList.remove('hidden');
      return;
    }

      resultsList.innerHTML = products.map((product) => {
      const canonicalHandle = getCanonicalProductHandle(product);
      const imageUrl = getProductImage(product);
      const price = product.price !== null && product.price !== undefined && product.price !== ''
        ? formatSearchPrice(product.price)
        : '';
      const image = imageUrl
        ? `<img src="${escapeHTML(imageUrl)}" alt="" class="pv-search__result-img" width="40" height="40" loading="lazy">`
        : '';

      return `
        <li class="pv-search__result" role="option" aria-selected="false" data-handle="${escapeHTML(canonicalHandle)}" data-slot="${slotIndex}" tabindex="0">
          ${image}
          <span class="pv-search__result-title">${escapeHTML(product.title)}</span>
          ${price ? `<span class="pv-search__result-price">${price}</span>` : ''}
        </li>
      `;
    }).join('');

    resultsList.classList.remove('hidden');

    resultsList.querySelectorAll('.pv-search__result').forEach((item) => {
      item.addEventListener('click', () => {
        loadProductIntoSlot(Number.parseInt(item.dataset.slot, 10), item.dataset.handle);
        hideSearchResults(slotIndex);
      });

      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          item.click();
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          item.nextElementSibling?.focus();
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          (item.previousElementSibling || document.querySelector(`.pv-search__input[data-slot="${slotIndex}"]`))?.focus();
        }
      });
    });
  }

  function hideSearchResults(slotIndex) {
    const results = document.getElementById(`pvSearchResults${slotIndex}`);
    if (results) {
      results.innerHTML = '';
      results.classList.add('hidden');
    }
  }

  function focusFirstResult(slotIndex) {
    document.querySelector(`#pvSearchResults${slotIndex} .pv-search__result`)?.focus();
  }

  // ---------------------------------------------------------------------------
  // Vergleichstabelle
  // ---------------------------------------------------------------------------

  function getProductMetafields(product) {
  const metafields = {
    ...(product?.product?.metafields || {}),
    ...(product?.metafields || {})
  };

  return normalizeMetafields(metafields);
}

  function getAttributeValue(product, key) {
    const metafields = getProductMetafields(product);

    if (key === 'variant_price') {
      if (hasValue(metafields.variant_price)) {
        return metafields.variant_price;
      }

      const variantPriceCents = Number(product?.variant?.price);
      return Number.isFinite(variantPriceCents)
        ? variantPriceCents / 100
        : null;
    }

    const value = metafields[key];

if (
  value &&
  typeof value === 'object' &&
  Object.prototype.hasOwnProperty.call(value, 'value')
) {
  return value.value;
}

return value ?? null;
  }

  function getMatchingAttributes(products, attributes) {
    return attributes.filter((attribute) => {
      return products.filter((product) => {
        return hasValue(getAttributeValue(product, attribute.key));
      }).length >= 2;
    });
  }

  function getFallbackTableConfig(category) {
    const common = [
      { key: 'variant_price', label: 'Preis', unit: '€', highlight: 'min' },
      { key: 'gewicht', label: 'Gewicht', unit: 'kg', highlight: 'min' },
      { key: 'garantie', label: 'Garantie', unit: 'Jahre', highlight: 'max' },
      { key: 'serie', label: 'Serie', unit: '', highlight: false },
      { key: 'material', label: 'Material', unit: '', highlight: false }
    ];

    if (category === 'Solarmodul') {
      return [
        { key: 'leistung_w', label: 'Leistung', unit: 'Wp', highlight: 'max' },
        { key: 'modulwirkungsgrad', label: 'Wirkungsgrad', unit: '%', highlight: 'max' },
        { key: 'solarzellentyp', label: 'Solarzellentyp', unit: '', highlight: false },
        { key: 'spannung_umpp', label: 'Spannung Umpp', unit: 'V', highlight: false },
        { key: 'strom_impp', label: 'Strom Impp', unit: 'A', highlight: false },
        ...common
      ];
    }

    if (category === 'Powerstation') {
      return [
        { key: 'kapazitaet_wh', label: 'Kapazität', unit: 'Wh', highlight: 'max' },
        { key: 'batterie_typ', label: 'Batterietyp', unit: '', highlight: false },
        { key: 'dauerleistung_ac', label: 'Dauerleistung AC', unit: 'W', highlight: 'max' },
        { key: 'anschlusse_notstrom', label: 'Anschlüsse & Notstromfunktionen', unit: '', highlight: false },
        ...common
      ];
    }

    if (category === 'Batterie') {
      return [
        { key: 'batteriekapazitaet_ah', label: 'Batteriekapazität', unit: 'Ah', highlight: 'max' },
        { key: 'batterie_typ', label: 'Batterie-Typ', unit: '', highlight: false },
        { key: 'batteriespannung', label: 'Batteriespannung', unit: 'V', highlight: false },
        ...common
      ];
    }

    return common;
  }

  function renderComparisonTable() {
    const tableWrapper = document.getElementById('pvTableWrapper');
    const tableHead = document.getElementById('pvTableHead');
    const tableBody = document.getElementById('pvTableBody');
    if (!tableWrapper || !tableHead || !tableBody) return;

    const activeProducts = state.slots.filter(Boolean);
    if (activeProducts.length < 2) {
      tableWrapper.classList.add('hidden');
      renderCtaBar([]);
      return;
    }

    const detectedCategory = activeProducts
  .map((product) => resolveCategory(product))
  .find(Boolean);

    const activeCategory = normalizeCategory(
  state.category ||
  detectedCategory ||
  window.SOLARKONTOR_COMPARE?.settings?.defaultCategory ||
  'Solarmodul'
  );

    const configuredAttributes = getActiveCategoryConfig();
    const categoryAttributes = configuredAttributes.length
      ? configuredAttributes
      : getFallbackTableConfig(activeCategory);

    const matchingAttributes = getMatchingAttributes(
      activeProducts,
      categoryAttributes
    );

    if (!matchingAttributes.length) {
      tableWrapper.classList.add('hidden');
      renderCtaBar(activeProducts);
      return;
    }

    tableHead.innerHTML = `
      <tr>
        <th class="pv-table__attr-col" scope="col"></th>
        ${activeProducts.map((product) => `
          <th class="pv-table__product-col" scope="col">
            <a href="${escapeHTML(product.url)}" class="pv-table__product-link">${escapeHTML(product.title)}</a>
          </th>
        `).join('')}
      </tr>
    `;

    tableBody.innerHTML = matchingAttributes.map((attribute) => {
      const values = activeProducts.map((product) => getAttributeValue(product, attribute.key));
      const highlights = computeHighlights(values, attribute.highlight);
      const cells = activeProducts.map((product, index) => {
        const value = getAttributeValue(product, attribute.key);
        const empty = !hasValue(value);
        const displayValue = formatAttrValue(value, attribute.unit);

        return `
          <td class="pv-table__cell${highlights[index] ? ' pv-table__cell--best' : ''}${empty ? ' pv-table__cell--empty' : ''}">
            ${empty ? '–' : `<span class="pv-table__value">${displayValue}</span>${highlights[index] ? '<span class="pv-table__best-badge" aria-label="Bestwert">★</span>' : ''}`}
          </td>
        `;
      }).join('');

      return `
        <tr class="pv-table__row">
          <th class="pv-table__attr-label" scope="row">
            ${escapeHTML(attribute.label)}${attribute.unit ? ` <span class="pv-table__unit">(${escapeHTML(attribute.unit)})</span>` : ''}
          </th>
          ${cells}
        </tr>
      `;
    }).join('');

    tableWrapper.classList.remove('hidden');
    renderCtaBar(activeProducts);
  }

  function computeHighlights(values, mode) {
    if (!mode) return values.map(() => false);

    const numbers = values.map((value) => parseFloat(value));
    const valid = numbers.filter((number) => !Number.isNaN(number));
    if (valid.length < 2) return values.map(() => false);

    const target = mode === 'max' ? Math.max(...valid) : Math.min(...valid);
    return numbers.map((number) => !Number.isNaN(number) && number === target);
  }

  function renderCtaBar(activeProducts) {
    return;

    const bar = document.getElementById('pvCtaBar');
    const inner = document.getElementById('pvCtaBarInner');
    if (!bar || !inner) return;

    if (!activeProducts || activeProducts.length < 2) {
      bar.classList.add('hidden');
      inner.innerHTML = '';
      return;
    }

    inner.innerHTML = `
      <span class="pv-cta-bar__label">Direktkauf:</span>
      <div class="pv-cta-bar__buttons">
        ${activeProducts.map((product) => `
          <div class="pv-cta-bar__item">
            <span class="pv-cta-bar__product-name">${escapeHTML(truncate(product.title, 30))}</span>
            <button class="pv-cta-bar__btn button button--primary" data-variant-id="${escapeHTML(product.variant?.id)}" ${!product.variant?.available ? 'disabled aria-disabled="true"' : ''}>
              ${product.variant?.available ? 'In den Warenkorb' : 'Nicht verfügbar'}
            </button>
          </div>
        `).join('')}
      </div>
    `;

    inner.querySelectorAll('.pv-cta-bar__btn').forEach((button) => {
      button.addEventListener('click', () => addToCart(button.dataset.variantId, button));
    });

    bar.classList.remove('hidden');
  }

  // ---------------------------------------------------------------------------
  // Warenkorb
  // ---------------------------------------------------------------------------

  async function addToCart(variantId, buttonElement) {
    if (!variantId || !buttonElement) return;

    const originalHTML = buttonElement.innerHTML;
    buttonElement.disabled = true;
    buttonElement.innerHTML = 'Wird geladen ...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: Number.parseInt(variantId, 10), quantity: 1 })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      await updateCartCount();

      buttonElement.innerHTML = 'Hinzugefügt!';
      showToast('Produkt wurde in den Warenkorb gelegt.', 'success');

      setTimeout(() => {
        buttonElement.innerHTML = originalHTML;
        buttonElement.disabled = false;
      }, 2500);
    } catch (error) {
      console.error('[Vergleich] Cart-Fehler:', error);
      buttonElement.innerHTML = originalHTML;
      buttonElement.disabled = false;
      showToast('Fehler beim Hinzufügen. Bitte erneut versuchen.', 'error');
    }
  }

  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const cart = await response.json();

      document.querySelectorAll('[data-cart-count], .cart-count-bubble, .cart__count').forEach((element) => {
        element.textContent = cart.item_count;
        element.closest('[aria-hidden]')?.removeAttribute('aria-hidden');
      });
    } catch (error) {
      console.warn('[Vergleich] Cart-Zähler konnte nicht aktualisiert werden.');
    }
  }

  // ---------------------------------------------------------------------------
  // UI und Hilfsfunktionen
  // ---------------------------------------------------------------------------

  let toastTimer = null;

  function showToast(message, type = 'info') {
    const toast = document.getElementById('pvToast');
    if (!toast) return;

    clearTimeout(toastTimer);
    toast.className = `pv-toast pv-toast--${type}`;
    toast.textContent = message;
    toast.classList.remove('hidden');
    toastTimer = setTimeout(() => toast.classList.add('hidden'), 3500);
  }

  function updateUI() {
    const filledCount = state.slots.filter(Boolean).length;
    document.getElementById('pvInfoBanner')?.classList.toggle('hidden', filledCount >= 2);
    updateCategoryLabel();
  }

  function updateCategoryLabel() {
    const label = document.getElementById('pvCategoryLabel');
    if (label) label.textContent = state.category || '–';
  }

  function initResetButton() {
    document.getElementById('pvResetBtn')?.addEventListener('click', clearAllSlots);
  }

  function updateURL() {
    const params = new URLSearchParams();
    const keys = ['p', 'p2', 'p3'];

    if (state.category) params.set('category', state.category);
    state.slots.forEach((product, index) => {
      if (product) params.set(keys[index], product.handle);
    });

    const query = params.toString();
    window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
  }

  function hasValue(value) {
    if (Array.isArray(value)) {
      return value.some((item) => hasValue(item));
    }

    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  function formatAttrValue(value, unit) {
    if (!hasValue(value)) return '–';

    // Shopify list.single_line_text_field values arrive as arrays.
    // They are displayed as one safe, readable list in the comparison cell.
    if (Array.isArray(value)) {
      const items = value
        .flatMap((item) => Array.isArray(item) ? item : [item])
        .map((item) => String(item ?? '').trim())
        .filter(Boolean);

      return items.length
        ? items.map((item) => escapeHTML(item)).join(', ')
        : '–';
    }

    if (unit === '€') {
      const number = parseFloat(value);
      if (!Number.isNaN(number)) {
        return `${number.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
      }
    }

    if (unit && unit !== '€') {
      const number = parseFloat(value);
      if (!Number.isNaN(number)) {
        const formatted = Number.isInteger(number) ? String(number) : String(parseFloat(number.toFixed(1)));
        return `${formatted} ${escapeHTML(unit)}`;
      }
    }

    return escapeHTML(unit ? `${String(value)} ${unit}` : String(value));
  }

  function formatMoney(cents) {
    const number = Number(cents);
    if (!Number.isFinite(number)) return '';

    return `${(number / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  function formatSearchPrice(value) {
    let number = parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(number)) return '';
    if (Number.isInteger(number) && Math.abs(number) >= 10000) number /= 100;

    return `${number.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  function normalizeImageUrl(value) {
    if (!value) return '';

    let url = value;
    if (typeof url === 'object') {
      url = url.src || url.url || url.original || '';
    }

    url = String(url).trim();
    if (!url) return '';

    if (
      (url.startsWith('"') && url.endsWith('"')) ||
      (url.startsWith("'") && url.endsWith("'"))
    ) {
      url = url.slice(1, -1);
    }

    url = url
      .replace(/\\\\/g, '\\')
      .replace(/\\\//g, '/')
      .replace(/\\u0026/gi, '&')
      .replace(/&amp;/gi, '&');

    if (url.startsWith('//')) url = `https:${url}`;
    if (url.startsWith('/')) url = `${window.location.origin}${url}`;

    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      console.warn('[Vergleich] Ungültige Bild-URL:', value);
      return '';
    }
  }

  function getProductImage(product) {
    if (!product) return '';

    const candidates = [
      product.image,
      product.featured_image,
      product.featuredImage,
      product.images?.[0]?.src,
      product.images?.[0],
      product.media?.[0]?.preview_image?.src,
      product.media?.[0]?.src
    ];

    const candidate = candidates.find((value) => {
      if (!value) return false;
      if (typeof value === 'object') {
        return Boolean(value.src || value.url || value.original);
      }
      return String(value).trim() !== '';
    });

    return normalizeImageUrl(candidate);
  }

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function truncate(value, maxLength) {
    const text = String(value || '');
    return text.length > maxLength ? `${text.substring(0, maxLength)}…` : text;
  }

  // ---------------------------------------------------------------------------
  // Start
  // ---------------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();